// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Dropdown, TextInput, TextArea, useToast } from '@loa/ui-components';
import axios from 'axios';
import { proposalApi } from '../../api/proposalApi';
import {
  CONTRIBUTION_FREQUENCY_OPTIONS,
  INVESTMENT_STYLE_OPTIONS,
  type Proposal,
  type RecommendedFundLine,
} from '../../types/proposal';
import type { Client } from '../../types/client';
import { useSoloClient } from '../../hooks/useSoloClient';
import { FundAllocationEditor } from '../funds/FundAllocationEditor';

type Props = Readonly<{
  open: boolean;
  onClose: () => void;
  clients: Client[];
  brokerageId: string;
  profileId: string;
  onCreated?: (proposal: Proposal) => void;
  existingProposal?: Proposal;
  onUpdated?: (proposal: Proposal) => void;
}>;

function initialLines(detail: Record<string, unknown>): RecommendedFundLine[] {
  const lines = detail.lines as Array<Record<string, unknown>> | undefined;
  if (!lines || lines.length === 0) return [];
  return lines.map((l) => ({
    fundKey: l.fundKey != null ? String(l.fundKey) : undefined,
    fundName: String(l.fundName ?? ''),
    allocationPercent: l.allocationPercent != null ? Number(l.allocationPercent) : undefined,
  }));
}

/** Compose (or edit) a FUND_INVESTMENT proposal with one or more fund lines. */
export const NewFundInvestmentProposalModal: React.FC<Props> = ({
  open,
  onClose,
  clients,
  profileId,
  onCreated,
  existingProposal,
  onUpdated,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const isEditMode = Boolean(existingProposal);
  const detail = (existingProposal?.details ?? {}) as Record<string, unknown>;
  const { isSolo, soloClient } = useSoloClient(clients);

  const [targetClientId, setTargetClientId] = useState(existingProposal?.targetClientId ?? '');

  // Solo relationships (Investment & Protection Proposals PRD RQ-9) never
  // show the Client selector — auto-select the only possible recipient.
  useEffect(() => {
    if (!isEditMode && !targetClientId && isSolo && soloClient) {
      setTargetClientId(soloClient.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isSolo, soloClient]);
  const [title, setTitle] = useState(existingProposal?.title ?? '');
  const [clientMessage, setClientMessage] = useState(existingProposal?.clientMessage ?? '');
  const [validUntil, setValidUntil] = useState(existingProposal?.validUntil ?? '');
  const [totalAmount, setTotalAmount] = useState(
    detail.totalInvestmentAmount != null ? String(detail.totalInvestmentAmount) : '',
  );
  const [investmentStyle, setInvestmentStyle] = useState(String(detail.investmentStyle ?? 'LUMP_SUM'));
  const [frequency, setFrequency] = useState(String(detail.contributionFrequency ?? 'MONTHLY'));
  const [rationale, setRationale] = useState(String(detail.rationale ?? ''));
  const [lines, setLines] = useState<RecommendedFundLine[]>(initialLines(detail));
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    (isEditMode || Boolean(targetClientId)) &&
    Boolean(totalAmount) &&
    Boolean(investmentStyle) &&
    lines.length > 0 &&
    lines.every((l) => Boolean(l.fundName));

  const buildDetails = (): Record<string, unknown> => ({
    totalInvestmentAmount: Number(totalAmount),
    currencyCode: 'EUR',
    investmentStyle,
    contributionFrequency: investmentStyle === 'REGULAR' ? frequency : undefined,
    rationale: rationale || undefined,
    lines: lines.map((l) => ({
      fundKey: l.fundKey,
      fundName: l.fundName,
      allocationPercent: l.allocationPercent,
    })),
  });

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEditMode && existingProposal) {
        const updated = await proposalApi.updateProposal(existingProposal.id, {
          title: title || undefined,
          clientMessage: clientMessage || undefined,
          validUntil: validUntil || undefined,
          details: buildDetails(),
        });
        toast.showToast('Proposal updated', 'success');
        onUpdated?.(updated);
        onClose();
      } else {
        const created = await proposalApi.createProposal({
          profileId,
          targetClientId,
          proposalType: 'FUND_INVESTMENT',
          title: title || undefined,
          clientMessage: clientMessage || undefined,
          validUntil: validUntil || undefined,
          details: buildDetails(),
        });
        toast.showToast('Proposal created', 'success');
        onCreated?.(created);
        navigate(`/relationships/${profileId}/proposals/${created.id}`);
      }
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string })?.message ?? err.message
        : 'Failed to save proposal';
      toast.showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const clientOptions = clients.map((c) => ({ value: c.id, text: `${c.firstName} ${c.lastName}` }));
  const styleOptions = INVESTMENT_STYLE_OPTIONS.map((o) => ({ value: o.value, text: o.label }));
  const frequencyOptions = CONTRIBUTION_FREQUENCY_OPTIONS.map((o) => ({ value: o.value, text: o.label }));

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header
        icon={isEditMode ? 'edit' : 'plus square outline'}
        title={isEditMode ? 'Edit Investment Proposal' : 'New Investment Proposal'}
        onClose={onClose}
      />
      <Modal.Content>
        <div data-testid="fund-investment-proposal-form" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!isEditMode && !isSolo && (
            <Dropdown label="Client" placeholder="Select a client" options={clientOptions}
              value={targetClientId} onChange={setTargetClientId} aria-label="Client" />
          )}
          <TextInput label="Title" value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} />
          <TextInput label="Total investment (EUR)" type="number" value={totalAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotalAmount(e.target.value)} />
          <Dropdown label="Investment style" options={styleOptions}
            value={investmentStyle} onChange={setInvestmentStyle} aria-label="Investment style" />
          {investmentStyle === 'REGULAR' && (
            <Dropdown label="Contribution frequency" options={frequencyOptions}
              value={frequency} onChange={setFrequency} aria-label="Contribution frequency" />
          )}

          <div data-testid="fund-lines">
            <strong>Funds</strong>
            <FundAllocationEditor value={lines} onChange={setLines} testId="fund-lines-editor" />
          </div>

          <TextInput label="Valid until (optional)" type="date" value={validUntil}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidUntil(e.target.value)} />
          <TextArea label="Message to client" value={clientMessage}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setClientMessage(e.target.value)} rows={3} />
          <TextArea label="Rationale" value={rationale}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRationale(e.target.value)} rows={3} />
        </div>
      </Modal.Content>
      <Modal.Actions
        primaryAction={{
          label: isEditMode ? 'Save changes' : 'Create proposal',
          onClick: handleSubmit,
          disabled: !canSubmit || submitting,
          loading: submitting,
        }}
        secondaryAction={{ label: 'Cancel', onClick: onClose }}
      />
    </Modal>
  );
};

export default NewFundInvestmentProposalModal;
